// Página de CONTACTO y soporte al cliente. Combina canales de contacto, un
// formulario (nombre, email, asunto, mensaje) y un FAQ de soporte. El envío del
// formulario es un stub listo para conectar con el endpoint de soporte/CRM.
// SEO: título + descripción + JSON-LD (FAQ + ContactPoint).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../ui/components.js';
import { IMG } from '../media.js';
import { Container, Eyebrow, FaqItem, SectionHeading } from '../components.js';
import { faqJsonLd, organizationJsonLd, SITE_URL, useSeo } from '../seo.js';

export function ContactView() {
  const { t } = useTranslation();
  const SUPPORT_FAQS = [
    { q: t('marketing.contact.faq1Q'), a: t('marketing.contact.faq1A') },
    { q: t('marketing.contact.faq2Q'), a: t('marketing.contact.faq2A') },
    { q: t('marketing.contact.faq3Q'), a: t('marketing.contact.faq3A') },
    { q: t('marketing.contact.faq4Q'), a: t('marketing.contact.faq4A') },
  ];

  const CONTACT_METHODS = [
    { icon: 'mail', title: t('marketing.contact.methodEmail'), value: 'djsaands@gmail.com', href: 'mailto:djsaands@gmail.com' },
    { icon: 'forum', title: t('marketing.contact.methodHelp'), value: t('marketing.contact.methodHelpValue'), href: '#faq' },
    { icon: 'schedule', title: t('marketing.contact.methodSchedule'), value: t('marketing.contact.methodScheduleValue'), href: undefined },
  ];

  const SUBJECTS = [
    t('marketing.contact.subjectGeneral'),
    t('marketing.contact.subjectTech'),
    t('marketing.contact.subjectBilling'),
    t('marketing.contact.subjectBug'),
    t('marketing.contact.subjectSuggestion'),
  ];

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState(SUBJECTS[0]);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useSeo({
    title: t('marketing.contact.seoTitle'),
    description: t('marketing.contact.seoDescription'),
    path: '/contacto',
    image: IMG.turntable,
    keywords: t('marketing.contact.seoKeywords', { returnObjects: true }) as string[],
    jsonLd: [
      faqJsonLd(SUPPORT_FAQS),
      {
        ...organizationJsonLd(),
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          email: 'djsaands@gmail.com',
          availableLanguage: ['Spanish', 'English'],
          url: `${SITE_URL}/contacto`,
        },
      },
    ],
  });

  const canSubmit = name.trim() !== '' && /\S+@\S+\.\S+/.test(email) && message.trim().length >= 10;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      // === PUNTO DE INTEGRACIÓN ===
      // Conectar con el endpoint público de contacto / soporte o un CRM:
      //   await api.contact.create({ name, email, subject, message });
      await new Promise((r) => setTimeout(r, 500));
      setSent(true);
    } catch {
      setError(t('marketing.contact.sendError'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <section className="py-space-xl lg:py-16 border-b border-border-subtle">
        <Container className="flex flex-col items-center text-center gap-space-md">
          <Eyebrow icon="support_agent">{t('marketing.contact.heroEyebrow')}</Eyebrow>
          <h1 className="font-display-hero text-display-hero-mobile lg:text-display-hero text-on-surface font-bold tracking-tight max-w-3xl text-balance">
            {t('marketing.contact.heroTitle')}
          </h1>
          <p className="font-body-md text-body-lg text-on-surface-variant max-w-2xl leading-relaxed">
            {t('marketing.contact.heroSubtitle')}
          </p>
        </Container>
      </section>

      <section className="py-space-xl">
        <Container className="grid lg:grid-cols-5 gap-space-xl items-start">
          {/* Canales */}
          <div className="lg:col-span-2 flex flex-col gap-space-md">
            <SectionHeading align="left" eyebrow={t('marketing.contact.channelsEyebrow')} eyebrowIcon="hub" title={t('marketing.contact.channelsTitle')} />
            <div className="flex flex-col gap-space-sm">
              {CONTACT_METHODS.map((m) => {
                const inner = (
                  <div className="surface-card flex items-center gap-space-md">
                    <span className="w-11 h-11 rounded-lg bg-primary-container/10 border border-primary-container/30 text-primary flex items-center justify-center shrink-0">
                      <Icon name={m.icon} className="text-[22px]" />
                    </span>
                    <div className="flex flex-col">
                      <span className="font-label-caps text-label-caps text-on-surface-variant uppercase tracking-widest">{m.title}</span>
                      <span className="font-body-md text-body-md text-on-surface">{m.value}</span>
                    </div>
                  </div>
                );
                return m.href ? (
                  <a key={m.title} href={m.href} className="block hover:opacity-90 transition-opacity">{inner}</a>
                ) : (
                  <div key={m.title}>{inner}</div>
                );
              })}
            </div>
          </div>

          {/* Formulario */}
          <div className="lg:col-span-3 surface-card flex flex-col gap-space-md">
            {sent ? (
              <div className="flex flex-col items-center text-center gap-space-md py-space-md">
                <span className="w-16 h-16 rounded-full bg-goldmine-mint/15 border border-goldmine-mint/40 flex items-center justify-center">
                  <Icon name="check_circle" className="text-goldmine-mint text-[32px]" />
                </span>
                <h2 className="font-headline-sm text-headline-md text-on-surface font-bold">{t('marketing.contact.sentTitle')}</h2>
                <p className="font-body-sm text-body-sm text-on-surface-variant max-w-sm leading-relaxed">
                  {t('marketing.contact.sentBody', {
                    name: name.split(' ')[0] || t('marketing.contact.sentFallbackName'),
                    email,
                  })}
                </p>
              </div>
            ) : (
              <>
                <h2 className="font-headline-sm text-headline-md text-on-surface font-bold">{t('marketing.contact.formTitle')}</h2>
                {error && (
                  <div className="p-space-sm rounded-lg bg-error-container/20 border border-error/40 text-error font-body-sm text-body-sm flex items-start gap-space-xs">
                    <Icon name="error" className="text-[18px] shrink-0" />
                    {error}
                  </div>
                )}
                <form onSubmit={submit} className="flex flex-col gap-space-md">
                  <div className="grid sm:grid-cols-2 gap-space-md">
                    <div className="flex flex-col gap-space-xs">
                      <label htmlFor="cname" className="field-label">{t('marketing.contact.nameLabel')}</label>
                      <div className="field-slot">
                        <input id="cname" className="field-input" value={name} onChange={(e) => setName(e.target.value)} required />
                      </div>
                    </div>
                    <div className="flex flex-col gap-space-xs">
                      <label htmlFor="cemail" className="field-label">{t('marketing.contact.emailLabel')}</label>
                      <div className="field-slot">
                        <input id="cemail" type="email" className="field-input" value={email} onChange={(e) => setEmail(e.target.value)} required />
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-space-xs">
                    <label htmlFor="csubject" className="field-label">{t('marketing.contact.subjectLabel')}</label>
                    <div className="field-slot">
                      <select id="csubject" className="field-input" value={subject} onChange={(e) => setSubject(e.target.value)}>
                        {SUBJECTS.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex flex-col gap-space-xs">
                    <label htmlFor="cmsg" className="field-label">{t('marketing.contact.messageLabel')}</label>
                    <textarea
                      id="cmsg"
                      rows={5}
                      className="w-full bg-surface-deck border border-border-subtle rounded-lg py-3 px-space-md text-on-surface font-body-md text-body-md focus:outline-none focus:border-border-focus resize-y"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={t('marketing.contact.messagePlaceholder')}
                      required
                    />
                  </div>
                  <button type="submit" className="btn-cyan w-full" disabled={!canSubmit || busy}>
                    <Icon name="send" className="text-[20px]" />
                    {busy ? t('marketing.contact.sending') : t('marketing.contact.send')}
                  </button>
                </form>
              </>
            )}
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-space-xl bg-surface-deck border-y border-border-subtle scroll-mt-24">
        <Container className="flex flex-col gap-space-lg max-w-3xl">
          <SectionHeading eyebrow={t('marketing.contact.faqEyebrow')} eyebrowIcon="quiz" title={t('marketing.contact.faqTitle')} />
          <div className="flex flex-col gap-space-sm">
            {SUPPORT_FAQS.map((f) => (
              <FaqItem key={f.q} q={f.q} a={f.a} />
            ))}
          </div>
        </Container>
      </section>
    </>
  );
}
